import { strictify } from '~/utils/strictify'
import { requestGeneratedResource } from '~/services/api/requestGeneratedResource'
import { createGeneratedResourceOperation } from '~/services/api/createGeneratedResourceOperation'
import { z } from 'zod'
import { pendingTwoFactorChallenge } from '~/utils/pendingTwoFactorChallenge'
import { sanctumTokenStorage } from '~/utils/sanctumTokenStorage'
import { SessionTokenError } from '~/utils/SessionTokenError'
import { buildMultipartBody } from '~/utils/buildMultipartBody'
import { AuthLogin } from '~/services/api/contracts/requests/AuthLogin'
import { DashboardCreateUser } from '~/services/api/contracts/requests/DashboardCreateUser'
import { DashboardUpdateProfile } from '~/services/api/contracts/requests/DashboardUpdateProfile'
import { DashboardUpdateUser } from '~/services/api/contracts/requests/DashboardUpdateUser'
import { DashboardUsers } from '~/services/api/contracts/requests/DashboardUsers'
import { DashboardUploadAvatar } from '~/services/api/contracts/requests/DashboardUploadAvatar'
import { PartnerUserInvitationAccept } from '~/services/api/contracts/requests/PartnerUserInvitationAccept'
import { PartnerUserInvitationAcceptOrganizer } from '~/services/api/contracts/requests/PartnerUserInvitationAcceptOrganizer'
import { PartnerUserInvitationShow } from '~/services/api/contracts/requests/PartnerUserInvitationShow'
import { PartnerUserInvitationShowOrganizer } from '~/services/api/contracts/requests/PartnerUserInvitationShowOrganizer'
import { PasswordResetReset } from '~/services/api/contracts/requests/PasswordResetReset'
import { PasswordResetSendResetLink } from '~/services/api/contracts/requests/PasswordResetSendResetLink'
import type {
  AcceptOrganizerUserInvitePayload,
  AcceptPartnerUserInvitePayload,
  ForgotPasswordPayload,
  LoginPayload,
  OrganizerUserInviteMetadataResponse,
  PartnerUserInviteMetadataResponse,
  ResetPasswordPayload
} from './types'
import {
  AuthLoginSuccessSchema,
  DashboardCreateUserSuccessSchema,
  DashboardDeleteAvatarSuccessSchema,
  DashboardRolePermissionsPathSchema,
  DashboardRolePermissionsSuccessSchema,
  DashboardRolesSuccessSchema,
  DashboardUpdateProfileSuccessSchema,
  DashboardUpdateUserPathSchema,
  DashboardUpdateUserSuccessSchema,
  DashboardUploadAvatarSuccessSchema,
  DashboardUserDetailsPathSchema,
  DashboardUserDetailsSuccessSchema,
  DashboardUsersSuccessSchema,
  PartnerUserInvitationAcceptOrganizerSuccessSchema,
  PartnerUserInvitationAcceptSuccessSchema,
  PartnerUserInvitationCancelPathSchema,
  PartnerUserInvitationCancelSuccessSchema,
  PartnerUserInvitationDestroyPathSchema,
  PartnerUserInvitationDestroySuccessSchema,
  PartnerUserInvitationResendPathSchema,
  PartnerUserInvitationResendSuccessSchema,
  PartnerUserInvitationShowOrganizerSuccessSchema,
  PartnerUserInvitationShowSuccessSchema,
  PartnerUserInvitationStoreRequestSchema,
  PartnerUserInvitationStoreSuccessSchema,
  PasswordResetResetSuccessSchema,
  PasswordResetSendResetLinkSuccessSchema
} from '~/services/api/generated'
import type {
  DashboardRolePermissionsSuccess,
  DashboardRolePermissionsPath,
  DashboardUpdateUserPath,
  DashboardUpdateUserRequest,
  DashboardUsersQuery,
  DashboardUserDetailsPath,
  PartnerUserInvitationCancelPath,
  PartnerUserInvitationDestroyPath,
  PartnerUserInvitationResendPath,
  PartnerUserInvitationShowOrganizerSuccess,
  PartnerUserInvitationShowSuccess,
  PartnerUserInvitationStoreRequest,
  PasswordResetResetSuccess,
  UserDto
} from '~/services/api/generated'

/**
 * Narrower than the generated `PasswordResetResetSuccessSchema`
 * (`result_code: z.string()`, since OpenAPI has no way to declare the
 * literal from a plain PHP string return type). Re-validating with
 * `z.literal` here -- after the generated schema has already validated the
 * wire shape -- is the boundary normalization step that gives callers a
 * `ResultCode` literal instead of a widened `string`, mirroring
 * transport.ts's `ForgotPasswordResultCodeSchema`.
 */
const ResetPasswordResultCodeSchema = z.literal('PASSWORD_RESET_COMPLETED')

type ResetPasswordResult = Omit<PasswordResetResetSuccess, 'result_code'> & {
  result_code: z.infer<typeof ResetPasswordResultCodeSchema>
}

// --- Invites -----------------------------------------------------------------------

// The API now returns expires_minutes as a genuine integer (or null when the
// invite never expires) instead of a numeric string, so this only needs a
// safe-integer guard, not string parsing.
const projectInviteExpiry = (expiresMinutes: number | null): number | null => {
  if (expiresMinutes === null) return null
  if (!Number.isSafeInteger(expiresMinutes)) {
    throw new TypeError('Invalid invitation metadata response: expires_minutes must be an integer.')
  }
  return expiresMinutes
}

export const normalizePartnerUserInviteMetadataResponse = (
  value: PartnerUserInvitationShowSuccess
): PartnerUserInviteMetadataResponse => {
  return {
    data: {
      ...value.data,
      expires_minutes: projectInviteExpiry(value.data.expires_minutes)
    }
  }
}

export const normalizeOrganizerUserInviteMetadataResponse = (
  value: PartnerUserInvitationShowOrganizerSuccess
): OrganizerUserInviteMetadataResponse => {
  return {
    data: {
      ...value.data,
      expires_minutes: projectInviteExpiry(value.data.expires_minutes)
    }
  }
}

// --- Auth / profile ------------------------------------------------------------------

export function useUserService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/dashboard`
  const baseUserURL = `${baseURL}/user`
  const publicApiBaseURL = `${useRuntimeConfig().public.apiBaseUrl}/api`
  const sanctumClient = useApiRequest()

  /**
   * Authenticate with the standard app login endpoint.
   */
  async function login(payload: LoginPayload) {
    const request = AuthLogin({}).parse(payload)
    const response = await sanctumClient(`${publicApiBaseURL}/auth/login`, {
      method: 'POST',
      body: request
    })
    const parsed = strictify(AuthLoginSuccessSchema).parse(response)
    if ('challenge_token' in parsed.data) {
      // Held in memory only, never persisted -- see pendingTwoFactorChallenge.
      pendingTwoFactorChallenge.set(parsed.data.challenge_token, request.remember ?? false)
    } else {
      // A non-2FA response makes any earlier abandoned challenge handoff stale.
      pendingTwoFactorChallenge.clear()
      if ('token' in parsed.data && parsed.data.token.length > 0) {
        await sanctumTokenStorage.set(useNuxtApp(), parsed.data.token, request.remember ?? false)
      } else {
        throw new SessionTokenError('token-missing')
      }
    }
    return parsed.data
  }

  /**
   * Request a password reset link. Backend returns the same success message
   * whether the email exists or not.
   */
  async function forgotPassword(payload: ForgotPasswordPayload) {
    const response = await sanctumClient(`${publicApiBaseURL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: PasswordResetSendResetLink({}).parse(payload)
    })
    return strictify(PasswordResetSendResetLinkSuccessSchema).parse(response)
  }

  /**
   * Reset password from a signed email link.
   */
  async function resetPassword(payload: ResetPasswordPayload): Promise<ResetPasswordResult> {
    const response = strictify(PasswordResetResetSuccessSchema).parse(
      await sanctumClient(`${publicApiBaseURL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: PasswordResetReset({}).parse(payload)
      })
    )
    return {
      ...response,
      result_code: ResetPasswordResultCodeSchema.parse(response.result_code)
    }
  }

  /** Update the current profile through the generated multipart contract. */
  async function updateProfile(payload?: z.output<ReturnType<typeof DashboardUpdateProfile>>) {
    const request = DashboardUpdateProfile({}).parse(payload ?? {})
    const formData = buildMultipartBody({
      avatar: request.avatar,
      email: request.email,
      last_name: request.last_name,
      name: request.name,
      phone: request.phone,
      timezone: request.timezone
    })

    return DashboardUpdateProfileSuccessSchema.parse(
      await sanctumClient(`${baseUserURL}/profile`, {
        method: 'PUT',
        body: formData
      })
    )
  }

  /** Upload an avatar through the generated multipart contract. */
  async function uploadAvatar(payload: z.output<ReturnType<typeof DashboardUploadAvatar>>) {
    const request = DashboardUploadAvatar({}).parse(payload)
    const formData = new FormData()
    formData.append('avatar', request.avatar)

    return DashboardUploadAvatarSuccessSchema.parse(
      await sanctumClient(`${baseUserURL}/avatar`, {
        method: 'POST',
        body: formData
      })
    )
  }

  /** Delete the current avatar and parse the generated success. */
  async function deleteAvatar() {
    return DashboardDeleteAvatarSuccessSchema.parse(
      await sanctumClient(`${baseUserURL}/avatar`, {
        method: 'DELETE'
      })
    )
  }

  /** Fetch a filtered, sorted, server-paginated page of the user directory. */
  async function getUsersPaginated(params?: DashboardUsersQuery) {
    const query = DashboardUsers({}).parse(params ?? {})
    const response = DashboardUsersSuccessSchema.parse(
      await sanctumClient(`${baseURL}/users`, { method: 'GET', params: query })
    )
    if (typeof response === 'string') {
      throw new TypeError('Invalid dashboard users response: expected a data envelope.')
    }

    return {
      data: response.data.items,
      pagination: response.data.pagination
    }
  }

  /**
   * Fetch a single user's details by ID.
   */
  async function getUserById(userId: DashboardUserDetailsPath['userId']): Promise<UserDto> {
    const path = DashboardUserDetailsPathSchema.strict().parse({ userId })
    const response = await sanctumClient(`${baseURL}/users/${path.userId}`)
    const parsed = DashboardUserDetailsSuccessSchema.parse(response)
    return parsed.data
  }

  /**
   * Create a new user.
   */
  const createUser = createGeneratedResourceOperation(
    sanctumClient,
    `${baseURL}/users`,
    DashboardCreateUserSuccessSchema,
    'POST',
    {
      channel: 'body',
      schema: DashboardCreateUser({}),
      select: (payload: z.output<ReturnType<typeof DashboardCreateUser>>) => payload
    }
  )

  /**
   * Create a partner-account user invitation.
   */
  const invitePartnerUser = createGeneratedResourceOperation(
    sanctumClient,
    `${baseURL}/users/invite`,
    PartnerUserInvitationStoreSuccessSchema,
    'POST',
    {
      channel: 'body',
      schema: strictify(PartnerUserInvitationStoreRequestSchema),
      select: (payload: PartnerUserInvitationStoreRequest) => payload
    }
  )

  /**
   * Update an existing user.
   */
  async function updateUser(
    userId: DashboardUpdateUserPath['userId'],
    payload: DashboardUpdateUserRequest
  ) {
    const path = DashboardUpdateUserPathSchema.strict().parse({ userId })
    const response = await sanctumClient(`${baseURL}/users/${path.userId}`, {
      method: 'PUT',
      body: DashboardUpdateUser({ invitedUser: payload.status === undefined }).parse(payload)
    })
    const parsed = DashboardUpdateUserSuccessSchema.parse(response)
    return parsed
  }

  /**
   * Fetch all roles with their permissions.
   */
  async function getRoles() {
    const response = await sanctumClient(`${baseURL}/roles`)
    return DashboardRolesSuccessSchema.parse(response).data
  }

  /**
   * Fetch the default permissions for a specific role.
   */
  async function getRolePermissions(
    roleName: DashboardRolePermissionsPath['role']
  ): Promise<DashboardRolePermissionsSuccess['data']> {
    const path = DashboardRolePermissionsPathSchema.strict().parse({ role: roleName })
    const response = await sanctumClient(`${baseURL}/roles/${path.role}/permissions`)
    return strictify(DashboardRolePermissionsSuccessSchema).parse(response).data
  }

  /** Builds a public invite-metadata lookup: parses the query, fetches, strict-parses, then normalizes. */
  const createInviteLookup = <
    QuerySchema extends z.ZodType<object>,
    SuccessSchema extends z.ZodType,
    Normalized
  >(
    path: string,
    querySchema: QuerySchema,
    successSchema: SuccessSchema,
    normalize: (success: z.output<SuccessSchema>) => Normalized
  ) => {
    const fetchInvite = createGeneratedResourceOperation(
      sanctumClient,
      `${publicApiBaseURL}/invites/${path}`,
      successSchema,
      'GET',
      { channel: 'query', schema: querySchema, select: (query: z.input<QuerySchema>) => query }
    )
    return async (query: z.input<QuerySchema>): Promise<Normalized> =>
      normalize(await fetchInvite(query))
  }

  /** Fetch public invitation metadata for the accept-invite flow. */
  const getPartnerUserInvite = createInviteLookup(
    'partner-user',
    PartnerUserInvitationShow({}),
    PartnerUserInvitationShowSuccessSchema,
    normalizePartnerUserInviteMetadataResponse
  )

  /** Fetch public invitation metadata for an organizer-account user invite. */
  const getOrganizerUserInvite = createInviteLookup(
    'organizer-user',
    PartnerUserInvitationShowOrganizer({}),
    PartnerUserInvitationShowOrganizerSuccessSchema,
    normalizeOrganizerUserInviteMetadataResponse
  )

  /**
   * Accept a partner-account user invitation.
   */
  const acceptPartnerUserInvite = createGeneratedResourceOperation(
    sanctumClient,
    `${publicApiBaseURL}/invites/partner-user/accept`,
    PartnerUserInvitationAcceptSuccessSchema,
    'POST',
    {
      channel: 'body',
      schema: PartnerUserInvitationAccept({}),
      select: (payload: AcceptPartnerUserInvitePayload) => payload
    }
  )

  /**
   * Accept an organizer-account user invitation.
   */
  const acceptOrganizerUserInvite = createGeneratedResourceOperation(
    sanctumClient,
    `${publicApiBaseURL}/invites/organizer-user/accept`,
    PartnerUserInvitationAcceptOrganizerSuccessSchema,
    'POST',
    {
      channel: 'body',
      schema: PartnerUserInvitationAcceptOrganizer({}),
      select: (payload: AcceptOrganizerUserInvitePayload) => payload
    }
  )

  /**
   * Resend a pending partner-account user invitation.
   */
  const resendPartnerUserInvite = createGeneratedResourceOperation(
    sanctumClient,
    {
      schema: PartnerUserInvitationResendPathSchema,
      parse: (userId: PartnerUserInvitationResendPath['userId']) => ({ userId }),
      buildUrl: (path) => `${baseURL}/users/${path.userId}/invite/resend`
    },
    PartnerUserInvitationResendSuccessSchema,
    'POST'
  )

  /**
   * Cancel a pending partner-account user invitation.
   */
  const cancelPartnerUserInvite = createGeneratedResourceOperation(
    sanctumClient,
    {
      schema: PartnerUserInvitationCancelPathSchema,
      parse: (userId: PartnerUserInvitationCancelPath['userId']) => ({ userId }),
      buildUrl: (path) => `${baseURL}/users/${path.userId}/invite`
    },
    PartnerUserInvitationCancelSuccessSchema,
    'DELETE'
  )

  /**
   * Delete an invited user whose invitation is still pending or has been cancelled.
   */
  async function deleteInvitedUser(
    userId: PartnerUserInvitationDestroyPath['userId']
  ): Promise<void> {
    await requestGeneratedResource(
      sanctumClient,
      {
        schema: PartnerUserInvitationDestroyPathSchema,
        value: { userId },
        buildUrl: (path) => `${baseURL}/users/${path.userId}`
      },
      PartnerUserInvitationDestroySuccessSchema,
      'DELETE'
    )
  }

  return {
    login,
    forgotPassword,
    resetPassword,
    updateProfile,
    uploadAvatar,
    deleteAvatar,
    getUsersPaginated,
    getUserById,
    createUser,
    invitePartnerUser,
    updateUser,
    getRoles,
    getRolePermissions,
    getPartnerUserInvite,
    getOrganizerUserInvite,
    acceptPartnerUserInvite,
    acceptOrganizerUserInvite,
    resendPartnerUserInvite,
    cancelPartnerUserInvite,
    deleteInvitedUser
  }
}
